import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Select, Switch, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Link2, Unlink } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColourClient, PartColourGroupClient, type ColourGroupKind, type ColourPartRef, type PartColoursDTO } from "../../api/configurator";
import { refusalMessage } from "../../api/manufacturing";
import { OpenRecord } from "../../components/OpenRecord";
import { PartPicker } from "../manufacturing/PartPicker";

export const COLOURS_KEY = ["manufacturing", "colour"];

export function colourGroupKindLabel(kind: ColourGroupKind, t: (key: string, fallback: string) => string): string {
  switch (kind) {
    case "PANEL":
      return t("colour.kind.panel", "Panels");
    case "EDGEBAND":
      return t("colour.kind.edgeband", "Edgebands");
    case "HARDWARE":
      return t("colour.kind.hardware", "Hardware");
    case "PIECE":
      return t("colour.kind.piece", "Cut pieces");
    default:
      return t("colour.kind.other", "Other");
  }
}

/** Every colour, for the pickers; settings data, edited rarely. */
export function useColours() {
  const { http } = useAuth();
  const client = useMemo(() => new ColourClient(http), [http]);
  return useQuery({ queryKey: [...COLOURS_KEY, "all"], queryFn: () => client.findAll(), staleTime: 5 * 60_000 });
}

/**
 * A part's colour and its sisters (design §3): the same part in the other
 * colours, one per colour. Here the part gets its colour (or is marked
 * neutral: raw MDF, used alike by every colour), joins or leaves its sisters,
 * and sees which colours have no sister yet. The suggestions are parts of the
 * same part group in other colours whose name less the colour is the same
 * (for a cut piece: the same letter and size).
 */
export function PartColoursTab({ partId, readOnly }: { partId: number; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new PartColourGroupClient(http), [http]);
  const key = [...COLOURS_KEY, "part", partId];
  const colours = useColours();
  const data = useQuery({ queryKey: key, queryFn: () => client.ofPart(partId) });

  const settle = (next: PartColoursDTO) => {
    queryClient.setQueryData(key, next);
    void queryClient.invalidateQueries({ queryKey: COLOURS_KEY });
  };
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const setColour = useMutation({
    mutationFn: (change: { colourId: number | null; neutral: boolean }) => client.setColour(partId, change.colourId, change.neutral),
    onSuccess: settle,
    onError: fail,
  });
  const link = useMutation({ mutationFn: (otherId: number) => client.link(partId, otherId), onSuccess: settle, onError: fail });
  const unlink = useMutation({
    mutationFn: async (target: number) => {
      await client.unlink(target);
      return client.ofPart(partId);
    },
    onSuccess: settle,
    onError: fail,
  });
  const busy = setColour.isPending || link.isPending || unlink.isPending;

  if (data.isLoading || !data.data) {
    return <div className="p-4 text-[13px] text-ink-muted lg:p-6">{t("table.loading", "Loading")}</div>;
  }
  const state = data.data;
  const options = (colours.data ?? [])
    .filter((colour) => colour.is_active || colour.id === state.part.colour_id)
    .map((colour) => ({ value: String(colour.id), label: colour.sku_code ? `${colour.code} (${colour.sku_code})` : colour.code }));

  return (
    <div className="flex flex-col gap-6 p-4 lg:p-6">
      <section className="flex flex-wrap items-end gap-4">
        <label className="flex min-w-64 flex-col gap-1 text-[13px]">
          <span className="font-medium">{t("colour.part.colour", "Colour")}</span>
          <Select
            options={options}
            placeholder={t("colour.part.none", "No colour")}
            value={state.part.colour_id == null ? "" : String(state.part.colour_id)}
            disabled={readOnly || busy || state.is_colour_neutral}
            onChange={(event) => setColour.mutate({ colourId: event.target.value === "" ? null : Number(event.target.value), neutral: false })}
          />
        </label>
        <Switch
          checked={state.is_colour_neutral}
          disabled={readOnly || busy}
          onChange={(neutral) => setColour.mutate({ colourId: null, neutral })}
          label={t("colour.part.neutral", "Colour neutral: every colour uses it alike")}
        />
        <Link to="/engineering/colours" className="ml-auto text-[13px] text-accent underline-offset-2 hover:underline">
          {t("colour.part.all", "All colours and links")}
        </Link>
      </section>

      {state.group ? (
        <section>
          <div className="mb-2 flex flex-wrap items-baseline gap-2">
            <h3 className="text-[14px] font-semibold">{t("colour.part.sisters", "Sisters")}</h3>
            <Badge size="sm" variant="neutral">{colourGroupKindLabel(state.group.kind, t)}</Badge>
            <span className="text-[13px] text-ink-muted">{state.group.name}</span>
          </div>
          <ul className="divide-y divide-line rounded-md border border-line">
            {state.sisters.map((sister) => (
              <SisterRow
                key={sister.part!.id}
                code={sister.colour_code}
                part={sister.part!}
                self={sister.part?.id === partId}
                onUnlink={readOnly ? null : () => unlink.mutate(sister.part!.id)}
                busy={busy}
              />
            ))}
          </ul>
          {state.missing.length > 0 && (
            <div className="mt-3">
              <p className="mb-1 text-[13px] text-ink-muted">
                {t("colour.part.missing", "No sister in {{n}} colours products are sold in:", { n: state.missing.length })}
              </p>
              <div className="flex flex-wrap gap-1">
                {state.missing.map((gap) => (
                  <Badge key={gap.colour_id} size="sm" variant="warning">{gap.colour_code}</Badge>
                ))}
              </div>
            </div>
          )}
        </section>
      ) : (
        !state.is_colour_neutral && (
          <p className="text-[13px] text-ink-muted">
            {state.part.colour_id == null
              ? t("colour.part.no-colour", "Give the part its colour first; then link it to the same part in other colours.")
              : t("colour.part.alone", "Not linked: no sister in another colour yet.")}
          </p>
        )
      )}

      {!readOnly && !state.is_colour_neutral && state.part.colour_id != null && (
        <section>
          <h3 className="mb-2 text-[14px] font-semibold">{t("colour.part.suggested", "Suggested sisters")}</h3>
          {state.suggestions.length === 0 ? (
            <p className="text-[13px] text-ink-muted">{t("colour.part.no-suggestion", "Nothing alike in another colour. Link one by its number below.")}</p>
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line">
              {state.suggestions.map((suggestion) => (
                <li key={suggestion.part.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-[13px]">
                  <Badge size="sm" variant="neutral">{suggestion.part.colour_code}</Badge>
                  <OpenRecord target={{ kind: "part", id: suggestion.part.id, tab: "colours" }} label={suggestion.part.number} />
                  <span className="font-mono">{suggestion.part.number}</span>
                  <span className="min-w-0 flex-1 truncate">{suggestion.part.name}</span>
                  <span className="text-ink-muted">
                    {suggestion.group_name
                      ? t("colour.part.in-group", "{{reason}}; in {{group}}", { reason: suggestion.reason, group: suggestion.group_name })
                      : suggestion.reason}
                  </span>
                  <Button size="sm" variant="secondary" icon={<Link2 className="size-4" />} disabled={busy} onClick={() => link.mutate(suggestion.part.id)}>
                    {t("colour.part.link", "Link")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 max-w-md">
            <PartPicker
              placeholder={t("colour.part.link-other", "Link another part as a sister")}
              disabled={busy}
              exclude={[partId, ...state.sisters.map((sister) => sister.part!.id)]}
              onPick={(picked) => link.mutate(picked.id)}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function SisterRow({ code, part, self, onUnlink, busy }: { code: string; part: ColourPartRef; self: boolean; onUnlink: (() => void) | null; busy: boolean }) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2 text-[13px]">
      <span className="w-40 shrink-0 font-medium">{code}</span>
      {self ? <span className="size-7 shrink-0" /> : <OpenRecord target={{ kind: "part", id: part.id, tab: "colours" }} label={part.number} />}
      <span className={self ? "font-mono font-semibold" : "font-mono"}>{part.number}</span>
      <span className="min-w-0 flex-1 truncate">{part.name}</span>
      {part.status !== "ACTIVE" && <Badge size="sm" variant="neutral">{t("part.status.inactive", "Inactive")}</Badge>}
      {self && <Badge size="sm" variant="info">{t("colour.part.this", "This part")}</Badge>}
      {onUnlink && (
        <Button size="sm" variant="ghost" icon={<Unlink className="size-4" />} disabled={busy} onClick={onUnlink}
          aria-label={t("colour.part.unlink", "Unlink")}>
          {t("colour.part.unlink", "Unlink")}
        </Button>
      )}
    </li>
  );
}
